import { useState } from 'react';
import { ListPageSkeleton } from '@/components/PageSkeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PERMISSIONS } from '@/constants/permissions';
import { useUserStore } from '@/stores/userStore';

/** 缓存为配置标签页时，预留只读表单与不显示列配置的数据源表格 */
function ConfigurationSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-7 w-28" />
      {/** 短剧库名称、主要地域、其他地域和关联 BC 账户 */}
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-20" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-full md:w-[400px]" />
            <Skeleton className="h-8 w-10 shrink-0" />
          </div>
        </div>
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-9 w-full md:w-[400px]" />
          </div>
        ))}
      </div>
      {/** 数据源表格直接位于配置区内，首次无数据时不显示分页 */}
      <div className="space-y-4">
        <Skeleton className="h-7 w-40" />
        <ListPageSkeleton
          columnCount={3}
          showPagination={false}
          filterArea={<></>}
          className="rounded-none border-0"
          contentClassName="gap-0 p-0"
        />
      </div>
    </div>
  );
}

/** 短剧列表代码加载时沿用缓存标签页和操作权限，仅展示对应首屏占位 */
export function PlayletListSkeleton() {
  // 仅读取页面既有标签缓存，避免进入配置或上传记录时先显示短剧表格
  const [activeTab] = useState(() => localStorage.getItem('playlet-list-active-tab') || 'video');
  const permissionCodes = useUserStore((state) => state.userInfo?.permissionCodes);
  const canUpload = permissionCodes?.includes(PERMISSIONS['短剧库管理/上传短剧']);
  const canDelete = permissionCodes?.includes(PERMISSIONS['短剧库管理/删除短剧']);
  const isUpload = activeTab === 'upload';

  return (
    <div className="p-6 space-y-6" aria-hidden="true">
      {/** 返回入口和页面标题 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
      <Card>
        <CardContent className="space-y-4">
          {/** 短剧列表、短剧库配置和上传记录标签 */}
          <div className="flex h-9 w-fit items-center gap-1">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-20" />
          </div>
          {activeTab === 'material' ? (
            <ConfigurationSkeleton />
          ) : (
            (activeTab === 'video' || isUpload) && (
              <div className="p-6 space-y-6">
                <ListPageSkeleton
                  columnCount={isUpload ? 8 : 11}
                  showPagination={false}
                  contentClassName="gap-4 p-6"
                  filterArea={
                    <div className="flex items-end justify-between gap-4 pb-4">
                      <div className="flex-1">
                        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                          {isUpload ? (
                            <div className="flex flex-wrap gap-2 md:gap-4 md:items-center">
                              {/** 上传记录保留文件名称搜索和重置入口 */}
                              <div className="relative w-full md:w-auto">
                                <Skeleton className="h-9 w-full md:w-[250px]" />
                              </div>
                              <Skeleton className="h-9 w-16" />
                            </div>
                          ) : (
                            <>
                              <div />
                              {/** 短剧列表按现有权限预留上传和批量删除入口 */}
                              <div className="flex flex-wrap gap-2 md:gap-4 md:items-center pr-4">
                                {canUpload && <Skeleton className="h-9 w-24" />}
                                {canDelete && <Skeleton className="h-9 w-24" />}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      <Skeleton className="h-9 w-24 shrink-0" />
                    </div>
                  }
                />
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
